/**
 * CR141: Attribute Help Dictionary
 * Provides meaning and example values for deal attributes in the Visibility Matrix
 */

export interface AttributeHelp {
  key: string;
  label: string;
  meaning: string;
  example: string;
}

export const ATTRIBUTE_HELP: Record<string, AttributeHelp> = {
  // Identity & Source
  batch_run_id: {
    key: "batch_run_id",
    label: "Batch Run ID",
    meaning: "Internal ID of the batch run that produced/updated the deal",
    example: "2e122755-6dd1-4a6b-9c22-1c8a7d2f0a13",
  },
  merchant_id: {
    key: "merchant_id",
    label: "Merchant ID",
    meaning: "Internal merchant identifier (links the deal to a merchant)",
    example: "earls",
  },
  merchant_name: {
    key: "merchant_name",
    label: "Merchant Name",
    meaning: "Display name of the merchant offering the deal",
    example: "Earls Kitchen + Bar",
  },
  dedupe_key: {
    key: "dedupe_key",
    label: "Dedupe Key",
    meaning: "Unique fingerprint used to identify duplicate deals across sources",
    example: "earls:bonus:50:100:2026-02",
  },

  // Deal Classification
  deal_type: {
    key: "deal_type",
    label: "Deal Type",
    meaning: "Canonical category of the deal",
    example: "BONUS (others: DISCOUNT, POINTS, IN_KIND)",
  },
  deal_subtype: {
    key: "deal_subtype",
    label: "Deal Subtype",
    meaning: "More specific classification within the deal type",
    example: "BONUS, DISCOUNT_PERCENT",
  },
  headline_mechanic: {
    key: "headline_mechanic",
    label: "Headline Mechanic",
    meaning: "Short summary of the deal mechanics shown in cards",
    example: "Spend $100, Get $20 Bonus",
  },
  offer_text: {
    key: "offer_text",
    label: "Offer Text",
    meaning: "Raw offer text as extracted from source",
    example: "Get a $20 bonus card when you buy $100 in gift cards",
  },

  // Value Mechanics
  bonus_value: {
    key: "bonus_value",
    label: "Bonus Value",
    meaning: "Bonus amount received (typically gift card bonus in $)",
    example: "50 (meaning 'get $50 bonus')",
  },
  purchase_amount: {
    key: "purchase_amount",
    label: "Purchase Amount",
    meaning: "Qualifying spend required to earn the bonus",
    example: "100 (meaning 'spend $100')",
  },
  discount_value: {
    key: "discount_value",
    label: "Discount Value",
    meaning: "Discount amount or percent (depending on deal subtype)",
    example: "10 (10% off) or 25 ($25 off)",
  },
  discount_percent: {
    key: "discount_percent",
    label: "Discount Percent",
    meaning: "Percentage discount on purchase",
    example: "15 (meaning 15% off)",
  },
  points_amount: {
    key: "points_amount",
    label: "Points Amount",
    meaning: "Number of loyalty points earned or required",
    example: "5000",
  },
  points_program: {
    key: "points_program",
    label: "Points Program",
    meaning: "Name of the loyalty program for points deals",
    example: "PC Optimum, Scene+",
  },
  computed_value_cad: {
    key: "computed_value_cad",
    label: "Computed Value (CAD)",
    meaning: "Calculated dollar value of the deal for comparison",
    example: "25.00",
  },
  effective_percent_back: {
    key: "effective_percent_back",
    label: "Effective % Back",
    meaning: "Calculated percentage return on spend",
    example: "20 (meaning 20% back)",
  },

  // Brands & Products
  purchase_brand: {
    key: "purchase_brand",
    label: "Purchase Brand",
    meaning: "Brand of gift card to purchase for the deal",
    example: "Uber, DoorDash",
  },
  reward_brand: {
    key: "reward_brand",
    label: "Reward Brand",
    meaning: "Brand of the bonus/reward received",
    example: "Best Buy, Home Depot",
  },
  reward_merchant: {
    key: "reward_merchant",
    label: "Reward Merchant",
    meaning: "Merchant where the reward can be redeemed",
    example: "Boston Pizza",
  },
  reward_type: {
    key: "reward_type",
    label: "Reward Type",
    meaning: "Type of reward payout (gift card, discount, etc.)",
    example: "GIFT_CARD, DISCOUNT_PERCENT, POINTS",
  },

  // Dates & Validity
  start_date: {
    key: "start_date",
    label: "Start Date",
    meaning: "When the promotion becomes active",
    example: "2026-01-20",
  },
  end_date: {
    key: "end_date",
    label: "End Date",
    meaning: "When the promotion expires (inclusive)",
    example: "2026-02-15",
  },
  validity_start_date: {
    key: "validity_start_date",
    label: "Validity Start",
    meaning: "Start of the deal validity window",
    example: "2026-01-25",
  },
  validity_end_date: {
    key: "validity_end_date",
    label: "Validity End",
    meaning: "End of the deal validity window",
    example: "2026-02-28",
  },
  validity_raw_text: {
    key: "validity_raw_text",
    label: "Validity (Raw)",
    meaning: "Original date text as extracted from source",
    example: "Valid Jan 20 - Feb 15, 2026",
  },

  // Confidence & Quality
  confidence: {
    key: "confidence",
    label: "Confidence",
    meaning: "Extraction confidence level (high, medium, low)",
    example: "high",
  },
  confidence_score: {
    key: "confidence_score",
    label: "Confidence Score",
    meaning: "Numeric extraction confidence (0-1). Higher = more reliable",
    example: "0.87",
  },
  extraction_method: {
    key: "extraction_method",
    label: "Extraction Method",
    meaning: "How the deal was extracted (AI, OCR, HTML, etc.)",
    example: "AI_DEEP_SCAN, OCR_HEURISTIC",
  },

  // Source & Evidence
  source_type: {
    key: "source_type",
    label: "Source Type",
    meaning: "Origin of the deal data",
    example: "FLYER, WEBSITE, FORUM",
  },
  source_url: {
    key: "source_url",
    label: "Source URL",
    meaning: "URL where the deal was found",
    example: "https://flyers.smartcanucks.ca/...",
  },
  evidence_id: {
    key: "evidence_id",
    label: "Evidence ID",
    meaning: "Pointer to evidence record (screenshots, OCR text)",
    example: "ev_01HZY8R3KQ9M2FJ4V8C2P7D1AX",
  },
  evidence_json: {
    key: "evidence_json",
    label: "Evidence JSON",
    meaning: "Structured evidence data (OCR snippets, screenshots)",
    example: '{"ocr_text": "...", "page_number": 3}',
  },
  provenance_json: {
    key: "provenance_json",
    label: "Provenance JSON",
    meaning: "Audit trail showing how the deal was processed",
    example: '{"steps": ["collect", "normalize", "dedup"]}',
  },

  // Status & Review
  is_unverified: {
    key: "is_unverified",
    label: "Is Unverified",
    meaning: "True if deal hasn't been human-verified yet",
    example: "true",
  },
  review_status: {
    key: "review_status",
    label: "Review Status",
    meaning: "Current review state of the deal",
    example: "ACCEPTED, PENDING, REJECTED",
  },
  publish_status: {
    key: "publish_status",
    label: "Publish Status",
    meaning: "Whether the deal has been published to production",
    example: "PUBLISHED, NOT_PUBLISHED",
  },
  visibility_status: {
    key: "visibility_status",
    label: "Visibility Status",
    meaning: "Current visibility state in production",
    example: "ACTIVE, EXPIRED, HIDDEN",
  },
  enrichment_status: {
    key: "enrichment_status",
    label: "Enrichment Status",
    meaning: "Whether enrichment steps are complete",
    example: "ENRICHED, PARTIAL, PENDING",
  },

  // Currency & Region
  currency: {
    key: "currency",
    label: "Currency",
    meaning: "Currency used for values in the deal",
    example: "CAD",
  },
  region: {
    key: "region",
    label: "Region",
    meaning: "Geographic region where deal is available",
    example: "CA, ON",
  },
  
  // Eligibility
  eligibility_scope: {
    key: "eligibility_scope",
    label: "Eligibility Scope",
    meaning: "Who can access this deal (public, corporate, members only, etc.)",
    example: "PUBLIC, CORPORATE, MEMBERS_ONLY",
  },

  // Technical Fields
  id: {
    key: "id",
    label: "ID",
    meaning: "Unique identifier for the deal record",
    example: "a1b2c3d4-e5f6-...",
  },
  created_at: {
    key: "created_at",
    label: "Created At",
    meaning: "When the deal record was first created",
    example: "2026-01-25T14:30:00Z",
  },
  updated_at: {
    key: "updated_at",
    label: "Updated At",
    meaning: "When the deal record was last modified",
    example: "2026-01-25T16:45:00Z",
  },
  staging_item_key: {
    key: "staging_item_key",
    label: "Staging Item Key",
    meaning: "Unique key for the staging record",
    example: "flyer:earls:2026-01-25:p3",
  },
  merchant_source_id: {
    key: "merchant_source_id",
    label: "Merchant Source ID",
    meaning: "ID of the merchant source configuration used",
    example: "src_earls_flyer_001",
  },
  detail_link: {
    key: "detail_link",
    label: "Detail Link",
    meaning: "Deep link to deal details page",
    example: "/deals/earls-bonus-jan2026",
  },
  deal_link: {
    key: "deal_link",
    label: "Deal Link",
    meaning: "External link to the deal or merchant page",
    example: "https://earls.ca/gift-cards",
  },
};

/**
 * Get help for an attribute, with fallback for unknown keys
 */
export function getAttributeHelp(key: string): AttributeHelp {
  return (
    ATTRIBUTE_HELP[key] || {
      key,
      label: formatKeyAsLabel(key),
      meaning: "No description available yet",
      example: "—",
    }
  );
}

/**
 * Convert snake_case key to Title Case label
 */
function formatKeyAsLabel(key: string): string {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
